(function () {
  'use strict';

  var body = document.body;
  if (!body) return;
  var privacyApi = null;
  var privacyReady = null;
  var consentState = null;
  var customScriptLoaded = false;
  var activeOverlay = null;
  var activeTrigger = null;
  var marketingInitialized = false;
  var marketingShownThisPage = false;
  var pageStartedAt = Date.now();
  var pendingRejectKey = 'gd:consent-reject-pending';
  var savingConsent = false;

  function storageGet(storage, key) {
    try { return storage.getItem(key); } catch (error) { return null; }
  }

  function storageSet(storage, key, value) {
    try { storage.setItem(key, value); } catch (error) { /* Privacy gates still work without storage. */ }
  }

  function asError(value, fallback) {
    if (value instanceof Error) return value;
    if (value && value.message) return new Error(String(value.message));
    return new Error(value ? String(value) : fallback);
  }

  function safeStorageGet(name, key) {
    try { return storageGet(window[name], key); } catch (error) { return null; }
  }

  function safeStorageSet(name, key, value) {
    try { storageSet(window[name], key, value); } catch (error) { /* Privacy gates still work without storage. */ }
  }

  function pendingRejection() {
    return safeStorageGet('sessionStorage', pendingRejectKey) === 'true';
  }

  function showConsentNotice(message) {
    var notice = document.querySelector('[data-gd-consent-notice]');
    if (!notice) return;
    notice.textContent = message;
    notice.hidden = false;
    if (typeof window.setTimeout === 'function') {
      window.setTimeout(function () { notice.hidden = true; }, 6500);
    }
  }

  function ensurePrivacyApi() {
    if (privacyReady) return privacyReady;
    privacyReady = new Promise(function (resolve, reject) {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
        reject(new Error('Shopify.loadFeatures não está disponível.'));
        return;
      }
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (error) {
          reject(asError(error, 'Não foi possível carregar a Customer Privacy API.'));
          return;
        }
        privacyApi = window.Shopify.customerPrivacy;
        if (!privacyApi || typeof privacyApi.currentVisitorConsent !== 'function' || typeof privacyApi.setTrackingConsent !== 'function') {
          reject(new Error('Customer Privacy API incompleta.'));
          return;
        }
        resolve(privacyApi);
      });
    }).catch(function (error) {
      privacyReady = null;
      throw error;
    });
    return privacyReady;
  }

  function privacyCall(method, value) {
    if (!privacyApi || typeof privacyApi[method] !== 'function') return Promise.reject(new Error('Customer Privacy API indisponível.'));
    return new Promise(function (resolve, reject) {
      var settled = false;
      function finish(result, error) {
        if (settled) return;
        settled = true;
        if (error) reject(error); else resolve(result);
      }
      function callbackResult(result) {
        if (result instanceof Error || (result && result.name === 'Error' && result.message)) finish(undefined, asError(result, 'A Customer Privacy API retornou um erro.'));
        else if (result && result.error) finish(undefined, asError(result.error, 'A Customer Privacy API retornou um erro.'));
        else finish(result);
      }
      try {
        var args = typeof value === 'undefined' ? [] : [value];
        args.push(callbackResult);
        var result = privacyApi[method].apply(privacyApi, args);
        if (result && typeof result.then === 'function') result.then(function (value) { finish(value); }, function (error) { finish(undefined, error); });
        else if (typeof result !== 'undefined') finish(result);
      } catch (error) { finish(undefined, error); }
    });
  }

  function consentValue(value) {
    if (value === true || value === 'yes') return true;
    if (value === false || value === 'no') return false;
    return null;
  }

  function normalizeConsent(value) {
    if (!value || typeof value !== 'object') return null;
    var source = value.consent || value;
    if (!source || typeof source !== 'object') return null;
    var keys = ['analytics', 'marketing', 'preferences'];
    var result = {};
    var found = false;
    keys.forEach(function (key) {
      var normalized = consentValue(source[key]);
      if (normalized !== null) { result[key] = normalized; found = true; }
    });
    return found ? result : null;
  }

  function readConsent() {
    return ensurePrivacyApi().then(function () { return privacyCall('currentVisitorConsent'); }).then(function (value) {
      consentState = pendingRejection() ? null : normalizeConsent(value);
      updateConsentGate();
      return consentState;
    });
  }

  function trackingConsent(value) {
    return ensurePrivacyApi()
      .then(function () { return privacyCall('setTrackingConsent', value); })
      .then(function () { return readConsent(); })
      .then(function (persisted) {
        if (!persisted) throw new Error('A Customer Privacy API não confirmou o consentimento salvo.');
        return persisted;
      });
  }

  function updateConsentGate() {
    if (body.dataset.gdConsentEnabled !== 'true') return;
    body.dataset.gdConsentResolved = consentState ? 'true' : 'false';
    body.dataset.gdAnalyticsConsent = consentState && consentState.analytics === true ? 'true' : 'false';
    body.dataset.gdMarketingConsent = consentState && consentState.marketing === true ? 'true' : 'false';
    body.dataset.gdPreferencesConsent = consentState && consentState.preferences === true ? 'true' : 'false';
    if (consentState && consentState.preferences === false) {
      try { window.localStorage.removeItem('gd:recently-viewed'); } catch (error) { /* No optional storage is created. */ }
    }
    document.dispatchEvent(new CustomEvent('gd:consent:updated', { detail: { consent: consentState } }));
  }

  function hasConsentFor(kind) {
    return consentState && consentState[kind] === true;
  }

  function focusables(node) {
    return Array.prototype.slice.call(node.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
  }

  function closeOverlay(overlay, force) {
    if (!overlay || (!force && (overlay.dataset.gdOverlay === 'age' || overlay.dataset.gdOverlay === 'consent'))) return;
    overlay.hidden = true;
    overlay.classList.remove('is-open');
    if (activeTrigger && document.contains(activeTrigger)) activeTrigger.focus();
    activeTrigger = null;
    activeOverlay = null;
    if (overlay.dataset.gdOverlay !== 'consent') {
      document.documentElement.classList.remove('gd-overlay-open');
      body.classList.remove('gd-overlay-open');
    }
  }

  function openOverlay(name, trigger) {
    var overlay = document.querySelector('[data-gd-overlay="' + name + '"]');
    if (!overlay || activeOverlay) return false;
    activeOverlay = overlay;
    activeTrigger = trigger || document.activeElement;
    overlay.hidden = false;
    overlay.classList.add('is-open');
    if (name !== 'consent') {
      document.documentElement.classList.add('gd-overlay-open');
      body.classList.add('gd-overlay-open');
      var first = focusables(overlay)[0];
      if (first) window.setTimeout(function () { first.focus(); }, 0);
    }
    return true;
  }

  function nextAfterGate() {
    var age = document.querySelector('[data-gd-overlay="age"]');
    var consent = document.querySelector('[data-gd-overlay="consent"]');
    if (age && !age.hidden) return;
    if (consent && !consent.hidden) return;
    if (body.dataset.gdConsentEnabled === 'true' && !consentState && !pendingRejection()) {
      openOverlay('consent');
      return;
    }
    initMarketing();
  }

  function setConsentStatus(message) {
    var status = document.querySelector('[data-gd-consent-status]');
    if (status) status.textContent = message || '';
  }

  function finishConsent(value) {
    savingConsent = false;
    setConsentStatus('');
    var overlay = document.querySelector('[data-gd-overlay="consent"]');
    closeOverlay(overlay, true);
    loadCustomScript();
    nextAfterGate();
  }

  function saveConsent(value) {
    if (savingConsent) return;
    savingConsent = true;
    setConsentStatus('Salvando sua escolha…');
    trackingConsent(value).then(finishConsent).catch(function () {
      savingConsent = false;
      if (value.analytics === false && value.marketing === false && value.preferences === false) {
        safeStorageSet('sessionStorage', pendingRejectKey, 'true');
        consentState = null;
        updateConsentGate();
        try { window.localStorage.removeItem('gd:recently-viewed'); } catch (error) { /* No optional storage is created. */ }
        closeOverlay(document.querySelector('[data-gd-overlay="consent"]'), true);
        showConsentNotice('Não foi possível salvar a recusa na Shopify. Cookies opcionais ficam desligados nesta visita.');
      } else {
        setConsentStatus('Não foi possível salvar. Cookies opcionais seguem desligados. Você pode recusar e continuar.');
      }
    });
  }

  function initConsent() {
    var overlay = document.querySelector('[data-gd-overlay="consent"]');
    if (!overlay) return Promise.resolve();
    var reject = overlay.querySelector('[data-gd-consent-reject]');
    var accept = overlay.querySelector('[data-gd-consent-accept]');
    if (!document.documentElement.dataset.gdVisitorConsentBound) {
      document.documentElement.dataset.gdVisitorConsentBound = 'true';
      var onVisitorConsent = function () {
        readConsent().then(function (state) {
          if (!state) return;
          var currentOverlay = document.querySelector('[data-gd-overlay="consent"]');
          if (currentOverlay && !currentOverlay.hidden) closeOverlay(currentOverlay, true);
          loadCustomScript();
          nextAfterGate();
        }).catch(function () { /* Keep the gate closed when the API cannot confirm the event. */ });
      };
      document.addEventListener('visitorConsentCollected', onVisitorConsent);
      if (window.addEventListener) window.addEventListener('visitorConsentCollected', onVisitorConsent);
    }
    if (reject) reject.addEventListener('click', function () {
      saveConsent({ analytics: false, marketing: false, preferences: false });
    });
    if (accept) accept.addEventListener('click', function () {
      saveConsent({ analytics: true, marketing: true, preferences: true });
    });
    if (pendingRejection()) {
      return ensurePrivacyApi()
        .then(function () {
          return privacyCall('setTrackingConsent', { analytics: false, marketing: false, preferences: false });
        })
        .then(function () {
          try { window.sessionStorage.removeItem(pendingRejectKey); } catch (error) { /* Retry on the next visit. */ }
          return readConsent();
        }).catch(function () {
          consentState = null;
          updateConsentGate();
          return null;
        });
    }
    return readConsent().catch(function () {
      consentState = null;
      updateConsentGate();
      return null;
    });
  }

  function startConsentGate() {
    var overlay = document.querySelector('[data-gd-overlay="consent"]');
    var promise = initConsent();
    if (body.dataset.gdConsentEnabled !== 'true' || !overlay) return promise;
    return promise.then(function (state) {
      if (state) {
        loadCustomScript();
        initMarketing();
      } else if (!pendingRejection() && !activeOverlay) {
        openOverlay('consent');
      }
      return state;
    });
  }

  function loadCustomScript() {
    if (customScriptLoaded || body.dataset.gdCustomJsEnabled !== 'true') return;
    var url = body.dataset.gdCustomJsUrl || '';
    if (!/^https:\/\//i.test(url)) return;
    if (!consentState || !hasConsentFor('marketing')) return;
    var script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.dataset.gdThemeCustomScript = 'true';
    script.addEventListener('error', function () { script.remove(); });
    document.head.appendChild(script);
    customScriptLoaded = true;
  }

  function marketingAllowed() {
    if (body.dataset.gdConsentEnabled === 'true') return hasConsentFor('marketing');
    return true;
  }

  function maybeMarketing() {
    showMarketing();
  }

  function popupEvent(name) {
    document.dispatchEvent(new CustomEvent(name));
    if (hasConsentFor('analytics')) {
      if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
      window.dataLayer.push({ event: name });
    }
  }

  function canShowMarketing(marketing) {
    if (!marketing || marketing.dataset.gdPopupReady !== 'true' || !marketingAllowed() || activeOverlay) return false;
    if (/(^|\/)(cart|checkout)(\/|$)/.test(window.location.pathname)) return false;
    if (marketingShownThisPage || safeStorageGet('sessionStorage', 'gd:marketing-shown') === 'true') return false;
    if (safeStorageGet('localStorage', 'gd:popup-converted') === 'true') return false;
    return Number(safeStorageGet('localStorage', 'gd:popup-dismissed-until') || 0) <= Date.now();
  }

  function showMarketing() {
    var marketing = document.querySelector('[data-gd-overlay="marketing"]');
    if (!canShowMarketing(marketing) || !openOverlay('marketing')) return;
    marketingShownThisPage = true;
    safeStorageSet('sessionStorage', 'gd:marketing-shown', 'true');
    popupEvent('popup_exibido');
  }

  function closeMarketing(marketing) {
    if (marketing.hidden) return;
    if (safeStorageGet('localStorage', 'gd:popup-converted') !== 'true') {
      safeStorageSet('localStorage', 'gd:popup-dismissed-until', String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    }
    popupEvent('popup_fechado');
    closeOverlay(marketing);
  }

  function formatPopupPhone(value) {
    var digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits ? '(' + digits : '';
    if (digits.length <= 7) return '(' + digits.slice(0, 2) + ') ' + digits.slice(2);
    return '(' + digits.slice(0, 2) + ') ' + digits.slice(2, 7) + '-' + digits.slice(7);
  }

  function applyPopupCoupon(marketing, coupon) {
    var status = marketing.querySelector('[data-gd-popup-discount-status]');
    if (!status || marketing.dataset.gdPopupHasCart !== 'true' || !coupon) return;
    var root = marketing.dataset.gdRootUrl || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: coupon })
    }).then(function (response) {
      if (!response.ok) throw new Error('Cupom não aplicado.');
      return response.json();
    }).then(function (cart) {
      var applied = (cart.discount_codes || []).some(function (discount) {
        return discount.code && discount.code.toLowerCase() === coupon.toLowerCase() && discount.applicable !== false;
      });
      status.textContent = applied ? 'Cupom aplicado ao carrinho. A condição será confirmada no checkout.' : 'Copie o cupom para tentar aplicá-lo no checkout.';
    }).catch(function () { status.textContent = 'Copie o cupom para tentar aplicá-lo no checkout.'; });
  }

  function initMarketing() {
    var marketing = document.querySelector('[data-gd-overlay="marketing"]');
    if (!marketing || marketingInitialized) return;
    marketingInitialized = true;
    marketing.querySelectorAll('[data-gd-overlay-close]').forEach(function (button) {
      button.addEventListener('click', function () { closeMarketing(marketing); });
    });
    marketing.addEventListener('click', function (event) {
      if (event.target === marketing) closeMarketing(marketing);
    });
    var phone = marketing.querySelector('[data-gd-popup-phone]');
    if (phone) phone.addEventListener('input', function () { phone.value = formatPopupPhone(phone.value); });
    var form = marketing.querySelector('form');
    if (form) form.addEventListener('submit', function (event) {
      if (!form.reportValidity()) { event.preventDefault(); return; }
      var note = form.querySelector('[data-gd-popup-origin-note]');
      if (note) note.value = 'Origem: popup_saida | Página: ' + window.location.pathname + ' | WhatsApp: ' + (phone ? phone.value : '') + ' | Consentimento para promoções/newsletter por e-mail e WhatsApp: sim';
      safeStorageSet('sessionStorage', 'gd:popup-pending', String(Date.now()));
    });
    var success = marketing.querySelector('[data-gd-popup-success]');
    var coupon = marketing.querySelector('[data-gd-popup-coupon]');
    var copy = marketing.querySelector('[data-gd-popup-copy]');
    if (copy && coupon) copy.addEventListener('click', function () {
      if (!navigator.clipboard || !navigator.clipboard.writeText) { coupon.setAttribute('tabindex', '0'); coupon.focus(); return; }
      navigator.clipboard.writeText(coupon.textContent.trim()).then(function () { copy.textContent = 'Cupom copiado!'; }, function () { coupon.setAttribute('tabindex', '0'); coupon.focus(); });
    });
    var pendingAt = Number(safeStorageGet('sessionStorage', 'gd:popup-pending') || 0);
    if (success && !success.hidden && pendingAt > 0 && Date.now() - pendingAt < 15 * 60 * 1000) {
      try { window.sessionStorage.removeItem('gd:popup-pending'); } catch (error) { /* O cadastro ainda é confirmado pela Shopify. */ }
      safeStorageSet('localStorage', 'gd:popup-converted', 'true');
      if (openOverlay('marketing')) popupEvent('popup_convertido');
      if (coupon) applyPopupCoupon(marketing, coupon.textContent.trim());
      return;
    }
    if (success && !success.hidden) {
      success.hidden = true;
      var fields = marketing.querySelector('[data-gd-popup-fields]');
      if (fields) fields.hidden = false;
    }
    if (marketing.querySelector('[data-gd-popup-error]')) {
      openOverlay('marketing');
      return;
    }
    if (body.dataset.gdExitIntentEnabled === 'true') {
      if (/(^|\/)(cart|checkout)(\/|$)/.test(window.location.pathname)) return;
      if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
        window.setTimeout(showMarketing, 35000);
        var previousY = window.scrollY;
        var previousTime = Date.now();
        window.addEventListener('scroll', function () {
          var now = Date.now();
          var scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable > 0 && window.scrollY / scrollable >= .6) showMarketing();
          if (previousY - window.scrollY > 160 && now - previousTime < 350) showMarketing();
          previousY = window.scrollY;
          previousTime = now;
        }, { passive: true });
      } else {
        document.addEventListener('mouseout', function (event) {
          if (event.relatedTarget || event.clientY > 0) return;
          if (Date.now() - pageStartedAt < Math.max(5, Number(marketing.dataset.gdExitDelay) || 5) * 1000) return;
          showMarketing();
        });
      }
    } else if (body.dataset.gdNewsletterEnabled === 'true') {
      window.setTimeout(maybeMarketing, 8000);
    }
  }

  function initAge() {
    var age = document.querySelector('[data-gd-overlay="age"]');
    if (!age) return Promise.resolve();
    var accepted = safeStorageGet('localStorage', 'gd:age-accepted') === 'true';
    if (accepted) { age.remove(); return Promise.resolve(); }
    openOverlay('age');
    var accept = age.querySelector('[data-gd-age-accept]');
    var reject = age.querySelector('[data-gd-age-reject]');
    if (accept) accept.addEventListener('click', function () {
      safeStorageSet('localStorage', 'gd:age-accepted', 'true');
      closeOverlay(age, true);
      age.remove();
      startConsentGate().then(nextAfterGate);
    });
    if (reject) reject.addEventListener('click', function () {
      var destination = age.dataset.gdRejectUrl;
      if (destination) window.location.href = destination;
      else {
        var disclaimer = age.querySelector('.gd-overlay__disclaimer');
        if (disclaimer) disclaimer.textContent = 'Acesso não liberado sem a idade mínima. Feche esta aba para sair.';
      }
    });
    return Promise.resolve();
  }

  function initCountdown() {
    var countdown = document.querySelector('[data-gd-countdown]');
    if (!countdown) return;
    var deadline = Date.parse(countdown.dataset.gdCountdownDeadline || '');
    var value = countdown.querySelector('[data-gd-countdown-value]');
    if (!Number.isFinite(deadline) || deadline <= Date.now()) {
      countdown.textContent = countdown.dataset.gdCountdownExpired || 'Esta condição terminou.';
      return;
    }
    function tick() {
      var remaining = deadline - Date.now();
      if (remaining <= 0) {
        countdown.textContent = countdown.dataset.gdCountdownExpired || 'Esta condição terminou.';
        window.clearInterval(timer);
        return;
      }
      var totalSeconds = Math.floor(remaining / 1000);
      var days = Math.floor(totalSeconds / 86400);
      var hours = Math.floor((totalSeconds % 86400) / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;
      if (value) value.textContent = (days ? days + 'd ' : '') + String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0');
    }
    var timer = window.setInterval(tick, 1000);
    tick();
  }

  var cleanupFixedNotification;
  function initFixedNotification() {
    if (cleanupFixedNotification) cleanupFixedNotification();
    var notification = document.querySelector('[data-gd-fixed-notification]');
    if (!notification) return;
    var observers = [];
    var sync;
    var header = document.querySelector('.shopify-section-group-header-group[id$="__header"], .gd-header');
    var updateHeaderOffset = function () {
      if (!header || !header.getBoundingClientRect || !body.style) return;
      body.style.setProperty('--gd-header-offset', Math.max(0, Math.ceil(header.getBoundingClientRect().height)) + 'px');
    };
    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset);
    if (window.ResizeObserver && header) {
      var resizeObserver = new window.ResizeObserver(updateHeaderOffset);
      resizeObserver.observe(header);
      observers.push(resizeObserver);
    }
    var close = notification.querySelector('[data-gd-fixed-notification-close]');
    var dismiss = function () { cleanupFixedNotification(); notification.remove(); };
    if (close) close.addEventListener('click', dismiss);
    var preview = document.querySelector('.gd-mobile-cart-preview[data-gd-mobile-cart-preview]');
    if (preview) {
      sync = function () {
        var hidden = Boolean(!preview.hidden || document.documentElement.classList.contains('gd-drawer-open'));
        if (notification.hidden !== hidden) notification.hidden = hidden;
      };
      sync();
      var previewObserver = new MutationObserver(sync);
      previewObserver.observe(preview, { attributes: true, attributeFilter: ['hidden', 'class'] });
      var rootObserver = new MutationObserver(sync);
      rootObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      observers.push(previewObserver, rootObserver);
      document.addEventListener('gd:cart:updated', sync);
    }
    cleanupFixedNotification = function () {
      observers.forEach(function (observer) { observer.disconnect(); });
      window.removeEventListener('resize', updateHeaderOffset);
      if (sync) document.removeEventListener('gd:cart:updated', sync);
      if (close) close.removeEventListener('click', dismiss);
      cleanupFixedNotification = null;
    };
  }

  function initPreloader() {
    if (window.Shopify && window.Shopify.designMode) return;
    if (body.dataset.gdPreloaderEnabled !== 'true') return;
    var preloader = document.createElement('div');
    preloader.className = 'gd-preloader';
    preloader.setAttribute('role', 'status');
    preloader.textContent = 'Carregando…';
    document.body.appendChild(preloader);
    var removed = false;
    var remove = function () {
      if (removed) return;
      removed = true;
      preloader.classList.add('is-done');
      window.setTimeout(function () { preloader.remove(); }, 260);
    };
    if (document.readyState === 'complete') remove(); else window.addEventListener('load', remove, { once: true });
    window.setTimeout(remove, 4000);
  }

  function initFocusHandling() {
    document.addEventListener('keydown', function (event) {
      if (!activeOverlay) return;
      if (event.key === 'Escape' && activeOverlay.dataset.gdOverlay === 'marketing') { closeMarketing(activeOverlay); return; }
      if (event.key !== 'Tab') return;
      var items = focusables(activeOverlay);
      if (!items.length) return;
      if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items[items.length - 1].focus(); }
      else if (!event.shiftKey && document.activeElement === items[items.length - 1]) { event.preventDefault(); items[0].focus(); }
    });
  }

  function start() {
    var designMode = Boolean(window.Shopify && window.Shopify.designMode);
    if (designMode) {
      document.documentElement.classList.add('shopify-design-mode');
      document.querySelectorAll('[data-gd-overlay]').forEach(function (overlay) {
        overlay.hidden = true;
        overlay.removeAttribute('open');
      });
      initFixedNotification();
      initCountdown();
      return;
    }
    initPreloader();
    initFixedNotification();
    initCountdown();
    initFocusHandling();
    initAge().then(function () {
      var age = document.querySelector('[data-gd-overlay="age"]');
      if (age && !age.hidden) return;
      return startConsentGate().then(function () {
        var consent = document.querySelector('[data-gd-overlay="consent"]');
        if (consent && !consent.hidden) return;
        loadCustomScript();
        initMarketing();
      });
    });
  }

  if (window.__GD_SETTINGS_TEST_HOOK__) {
    window.__GD_SETTINGS_TEST_HOOK__({
      ensurePrivacyApi: ensurePrivacyApi,
      normalizeConsent: normalizeConsent,
      readConsent: readConsent,
      trackingConsent: trackingConsent,
      updateConsentGate: updateConsentGate,
      formatPopupPhone: formatPopupPhone,
      canShowMarketing: canShowMarketing
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', initFixedNotification);
  document.addEventListener('shopify:section:unload', function () {
    if (cleanupFixedNotification) cleanupFixedNotification();
  });
})();